import { createAsyncThunk } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosProgressEvent } from 'axios';

import { apiSahSupplierInvoices } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';
import { download, downloadFile, get, post, put, remove, upload } from '~libs/genericAction';
import { createThunks, Params } from '~libs/reduxUtils';

export const typePrefix = `supplier-invoices`;

interface DownloadParams extends Params {
  onProgress: (value: string) => void;
}
const config = [
  { actionType: 'getAll', apiPath: apiSahSupplierInvoices, method: get },

  { actionType: 'createInvoice', apiPath: apiSahSupplierInvoices, method: post },
  { actionType: 'getEstimate', apiPath: `${apiSahSupplierInvoices}/estimate`, method: get },
  { actionType: 'getOne', apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}`, method: get },
  {
    actionType: 'getFile',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/files/${params.id}`,
    method: downloadFile,
  },
  {
    actionType: 'deleteFile',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/files/${params.id}`,
    method: remove,
  },
  { actionType: 'updateInvoice', apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}`, method: put },
  {
    actionType: 'updateStatus',
    apiPath: (params: Params) => `${apiSahSupplierInvoices}/${params.id}/update-status`,
    method: post,
  },
];

type UploadParams = {
  id: string;
  file: File;
  onProgress?: (progress: string | number) => void;
  additionalData?: Record<string, any>;
};

export const uploadInvoiceFile = createAsyncThunk(
  `${typePrefix}/uploadFile`,
  async ({ id, file, onProgress, additionalData }: UploadParams, { rejectWithValue }) => {
    try {
      return upload(`${apiSahSupplierInvoices}/${id}/files`, file, onProgress, additionalData);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);
export const exportAll = async ({ onProgress, params }: DownloadParams) =>
  download(`${apiSahSupplierInvoices}/export`, params, onProgress);

interface SupplierInvoiceUploadResponse {
  message?: string;
}

export const supplierInvoicesApi = createApi({
  reducerPath: 'supplierInvoicesApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahSupplierInvoices }),
  tagTypes: ['supplier-invoices'],
  endpoints: (builder) => ({
    supplierInvoiceUpload: builder.mutation<
      SupplierInvoiceUploadResponse,
      { formData: FormData; onProgress?: (prog: number) => void }
    >({
      query: ({ formData, onProgress }) => ({
        url: '/import',
        method: 'POST',
        body: formData,
        onUploadProgress: (evt: AxiosProgressEvent) => {
          if (onProgress && evt.total) {
            const percent = Math.round((evt.loaded / evt.total) * 100);
            onProgress(percent);
          }
        },
      }),
      invalidatesTags: (_result, _error) => [{ type: 'supplier-invoices', id: 'LIST' }],
      transformResponse: (payload) => unwrap<SupplierInvoiceUploadResponse>(payload),
    }),
  }),
});

export const { useSupplierInvoiceUploadMutation } = supplierInvoicesApi;

export const { getAll, createInvoice, updateInvoice, getFile, getEstimate, getOne, updateStatus, deleteFile } =
  createThunks(typePrefix, config);
